import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './EventFormModal.css';
import { EventSection } from '../data/upcomingEvents';
import { supabase } from '../lib/supabaseClient';

interface EventFormModalProps {
    mode: 'add' | 'edit';
    event?: EventSection;
    onClose: () => void;
    onSaved: () => void;
}

interface FormState {
    title: string;
    date: string;
    endDate: string;
    time: string;
    location: string;
    description: string;
}

const empty: FormState = {
    title: '',
    date: '',
    endDate: '',
    time: '',
    location: '',
    description: '',
};

const EventFormModal: React.FC<EventFormModalProps> = ({ mode, event, onClose, onSaved }) => {
    const [form, setForm] = useState<FormState>(empty);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (mode === 'edit' && event) {
            setForm({
                title: event.title,
                date: event.date,
                endDate: event.endDate ?? '',
                time: event.time ?? '',
                location: event.location ?? '',
                description: event.description ?? '',
            });
        } else {
            setForm(empty);
        }
    }, [mode, event]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSaving(true);

        const payload = {
            title: form.title.trim(),
            date: form.date,
            end_date: form.endDate || null,
            time: form.time.trim() || null,
            location: form.location.trim() || null,
            description: form.description.trim() || null,
        };

        let err;
        if (mode === 'add') {
            ({ error: err } = await supabase.from('events').insert(payload));
        } else {
            ({ error: err } = await supabase.from('events').update(payload).eq('id', event!.id));
        }

        setSaving(false);
        if (err) {
            setError(err.message);
        } else {
            onSaved();
            onClose();
        }
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return ReactDOM.createPortal(
        <div className="event-modal-backdrop" onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-labelledby="event-modal-title">
            <div className="event-modal">
                <button className="event-modal-close" onClick={onClose} aria-label="Close">×</button>
                <h2 id="event-modal-title" className="event-modal-title">
                    {mode === 'add' ? 'Add Event' : 'Edit Event'}
                </h2>
                <form onSubmit={handleSubmit} className="event-modal-form">
                    <label className="event-modal-label" htmlFor="em-title">Title *</label>
                    <input id="em-title" className="event-modal-input" name="title" type="text" value={form.title} onChange={handleChange} required />

                    <div className="event-modal-row">
                        <div className="event-modal-field">
                            <label className="event-modal-label" htmlFor="em-date">Date *</label>
                            <input id="em-date" className="event-modal-input" name="date" type="date" value={form.date} onChange={handleChange} required />
                        </div>
                        <div className="event-modal-field">
                            <label className="event-modal-label" htmlFor="em-end-date">End Date <span className="event-modal-optional">(optional)</span></label>
                            <input id="em-end-date" className="event-modal-input" name="endDate" type="date" value={form.endDate} onChange={handleChange} />
                        </div>
                    </div>

                    <label className="event-modal-label" htmlFor="em-time">Time <span className="event-modal-optional">(optional)</span></label>
                    <input id="em-time" className="event-modal-input" name="time" type="text" placeholder="e.g. 9:00 AM – 5:00 PM" value={form.time} onChange={handleChange} />

                    <label className="event-modal-label" htmlFor="em-location">Location <span className="event-modal-optional">(optional)</span></label>
                    <input id="em-location" className="event-modal-input" name="location" type="text" placeholder="Venue name and address" value={form.location} onChange={handleChange} />

                    <label className="event-modal-label" htmlFor="em-description">Description <span className="event-modal-optional">(optional)</span></label>
                    <textarea id="em-description" className="event-modal-textarea" name="description" rows={4} placeholder="Additional details about the event" value={form.description} onChange={handleChange} />

                    {error && <p className="event-modal-error">{error}</p>}

                    <div className="event-modal-actions">
                        <button type="button" className="event-modal-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="event-modal-submit" disabled={saving}>
                            {saving ? 'Saving…' : mode === 'add' ? 'Add Event' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default EventFormModal;
